import axios from "axios";

// Prefer the current browser origin so requests are always same-origin.
// This works for: preview (lead-hub-web.preview.emergentagent.com), production
// (lead-hub-web.emergent.host), and any custom domain (e.g. lms.losung360.com)
// pointed at the Emergent ingress — eliminating cross-origin / CORS issues.
// Falls back to REACT_APP_BACKEND_URL for non-browser contexts (SSR, tests).
const BASE =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" && window.location && window.location.origin);
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach bearer token if present (fallback when cookies blocked)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("lms_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail === "object") {
    if (typeof detail.detail === "string") return detail.detail;
    if (detail.missing_fields && Array.isArray(detail.missing_fields)) {
      const head = detail.detail || "Missing fields";
      return `${head}: ${detail.missing_fields.join(", ")}`;
    }
    if (typeof detail.msg === "string") return detail.msg;
    return JSON.stringify(detail);
  }
  return String(detail);
}

/**
 * Cached GET — for "almost-static" reference data (users, master values, saved views).
 * Returns cached value instantly if present + younger than `ttlMs`, then revalidates
 * in the background and resolves with the fresher result on next call.
 *
 * Use only for safe-to-cache endpoints. Mutations on the same data should call
 * `cachedGet.invalidate(url)` to drop the stale entry.
 */
const _cache = new Map(); // url -> { data, at }

export async function cachedGet(url, { ttlMs = 5 * 60 * 1000 } = {}) {
  const now = Date.now();
  const hit = _cache.get(url);
  if (hit && now - hit.at < ttlMs) {
    return { data: hit.data, cached: true };
  }
  const res = await api.get(url);
  _cache.set(url, { data: res.data, at: now });
  return { data: res.data, cached: false };
}

cachedGet.invalidate = (urlOrPredicate) => {
  if (typeof urlOrPredicate === "function") {
    for (const k of _cache.keys()) if (urlOrPredicate(k)) _cache.delete(k);
  } else {
    _cache.delete(urlOrPredicate);
  }
};

